import React from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import {connect} from "react-redux";
import {bindActionCreators, Dispatch} from "redux";
import SearchType, {
    BulkProducts,
    BulkSearchProductRequest,
    GetBulkProductSearchDataResponse,
    ProductInfo,
    ProductInfoResult,
    ProgramInput,
    StorageSeason,
} from "src/model/ProductMatch";
import MetricsUtil from "src/utils/MetricsUtil";
import ApplicationState from "src/model/ApplicationState";
import {
    KatBox,
    KatButton,
    KatCheckbox,
    KatDropdown, KatIcon,
    KatLabel,
    KatLink, KatOption,
    KatPagination, KatPopover,
    KatRadiobuttonGroup,
    KatTable,
    KatTableBody,
    KatTableCell,
    KatTableHead,
    KatTableRow
} from "@amzn/katal-react";
import RequestStatus from "src/model/RequestStatus";
import {
    addProduct,
    delAllProducts,
    delProduct,
    updateAllProducts,
    updateProduct,
    updatePrograms,
    updateStorageSeason
} from "src/actions/BulkProductAction";
import {
    bulkProgramName,
    countryProgramList,
    getProgramInventoryCountryListConfig, getProgramStoreCountryListConfig,
    PROGRAM_NAME_OF_FBA, programWithCeeParam, programWithInventoryAndStoreParam,
    SELECT_YOUR_PROGRAM
} from "src/config/Config";
import FormatUtil from "src/utils/FormatUtil";
import {setBulkProductDataQueryStart} from "src/actions/BulkProductSearchAction";

interface StateProps {
    bulkProductsRes: BulkProducts,
    bulkProductSearchRes: GetBulkProductSearchDataResponse,
}

interface OwnProps {
    readonly currentCountryCode: string,
    readonly metricsUtil?: MetricsUtil,
    handleNext: Function
}

const mapStateToProps = (state: ApplicationState): StateProps => ({
    bulkProductsRes: state.bulkProductsRes,
    bulkProductSearchRes: state.bulkProductSearchRes,
});

interface DispatchProps {
    readonly dispatchAddProduct: (product: ProductInfo) => void,
    readonly dispatchDelProduct: (product: ProductInfo) => void,
    readonly dispatchUpdateProduct: (product: ProductInfo, oldProduct: ProductInfo) => void,
    readonly dispatchUpdateAllProducts: (products: Array<ProductInfo>) => void,
    readonly dispatchDelAllProducts: () => void,
    readonly dispatchUpdatePrograms: (programs: Array<string>) => void,
    readonly dispatchStorageSeason: (season: StorageSeason) => void,
    readonly dispatchGetProduct: (request: BulkSearchProductRequest) => void,
}

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({
        dispatchAddProduct: addProduct,
        dispatchDelProduct: delProduct,
        dispatchUpdateProduct: updateProduct,
        dispatchUpdateAllProducts: updateAllProducts,
        dispatchDelAllProducts: delAllProducts,
        dispatchUpdatePrograms: updatePrograms,
        dispatchStorageSeason: updateStorageSeason,
        dispatchGetProduct: setBulkProductDataQueryStart,
    }, dispatch)
);

const emptyProductInfoResult: ProductInfoResult = {
    totalProductCount: 0,
    currentPage: 1,
    products: []
}

const MERCHANT_ITEM_PER_PAGE = 8;
const GENERAL_ITEM_PER_PAGE = 16;
const BULK_MAX_SIZE = 20;

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = StateProps & OwnProps & DispatchProps & WithTranslation;

interface State {
    searchKey: string,
    programs: Array<string>,
    storageSeason: StorageSeason,
    inventoryPlacedIn: Array<string>,
    cee: Array<boolean>,
}

export class BulkProductTableComponent extends React.Component<Props, State> {

    protected readonly metricsUtil: MetricsUtil;
    private defaultProgramInputMap: Map<string, ProgramInput>;


    constructor(props: Props) {
        super(props);

        this.state = {
            searchKey: '',
            programs: [PROGRAM_NAME_OF_FBA],
            storageSeason: this.props.bulkProductsRes.storageSeason,
            inventoryPlacedIn: this.props.currentCountryCode == "US" ? ["US", "US"] : ["DE", "DE"],
            cee: [true, true]
        };
        this.defaultProgramInputMap = new Map<string, ProgramInput>();
        this.defaultProgramInputMap.set(PROGRAM_NAME_OF_FBA, {});

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }

        this.handlePageChange = this.handlePageChange.bind(this);
        this.handleChangeInventory = this.handleChangeInventory.bind(this);
        this.handleChangeCee = this.handleChangeCee.bind(this);

    }

    getProducts(): ProductInfoResult {
        if (this.props.bulkProductSearchRes && this.props.bulkProductSearchRes.requestStatus === RequestStatus.SUCCESS) {
            return this.props.bulkProductSearchRes.productData.data;
        }
        return emptyProductInfoResult;
    }

    productTableRow(product: ProductInfo, index: string, number: number, disableCheck: boolean) {
        const {t} = this.props;
        return (
            <>
                {/*todo add sort; change to dataTable?*/}
                <KatTableRow key={"row" + index} className={"bulk-table-row-" + (number % 2)}>
                    <KatTableCell>
                        <KatCheckbox key={"checkBox" + index} checked={this.productInSelected(product)} disabled={disableCheck}
                                     onChange={(e) => {
                                         // avoid double checked
                                         e.currentTarget.checked = false;
                                         e.detail.checked ? this.handleSelectProduct(product) : this.props.dispatchDelProduct(product);
                                     }}/>
                    </KatTableCell>
                    <KatTableCell><img src={product.imageUrl} className="bulk-product-img"/></KatTableCell>
                    <KatTableCell>
                        <KatLink href={product.link} label={product.title} variant="link"/>
                        <p className="bulk-table-asin">{product.asin}</p>
                    </KatTableCell>
                    {/*todo add input if the value is zero*/}
                    <KatTableCell>{FormatUtil.formatNumberDisplay(product.length)} X {FormatUtil.formatNumberDisplay(product.width)} X {FormatUtil.formatNumberDisplay(product.height)} {product.dimensionUnitStringId ? t(product.dimensionUnitStringId) : ''} {FormatUtil.formatNumberDisplay(product.weight)} {product.weightUnitStringId ? t(product.weightUnitStringId) : ''}</KatTableCell>
                    {/* https://code.amazon.com/packages/FPXScripts/blobs/mainline/--/shellScripts/BackfillPublicDisplayCategoryG2S2Record.sh*/}
                    <KatTableCell>{t("FS-ProductCategory-" + product.gl.replace("_", "-") + "-FullName", t("FS-ProductCategory-" + product.gl + "-FullName", product.gl))}</KatTableCell>
                    <KatTableCell>{product.isAfn ? t("SC_FBA_Calc_bulk_amazon") : t("SC_FBA_Calc_bulk_merchant")}</KatTableCell>
                    <KatTableCell>{product.price}</KatTableCell>
                </KatTableRow>
            </>
        )
    }

    handleSelectProduct = (p: ProductInfo) => {
        const productInfo = {
            ...p,
            isNonPeakSelected: this.state.storageSeason == StorageSeason.NON_PEAK,
            programInputMap: new Map(this.defaultProgramInputMap)
        }
        this.props.dispatchAddProduct(productInfo);
    }

    handleSelectAll = (e: KatCheckbox.ChangeEvent) => {
        if (e.detail.checked) {
            // @ts-ignore
            this.props.bulkProductSearchRes.productData.data.products.filter(p => !this.productInSelected(p))
                .map((product: ProductInfo) => {
                        const productInfo = {
                            ...product,
                            isNonPeakSelected: this.state.storageSeason == StorageSeason.NON_PEAK,
                            programInputMap: new Map(this.defaultProgramInputMap)
                        }
                        this.props.dispatchAddProduct(productInfo)
                    }
                )
        } else {
            this.props.dispatchDelAllProducts()
        }
        this.publishCounter("selectAll", "click")
    }

    handleChangeProgram1 = (e: KatDropdown.ChangeEvent) => {
        const programs = [...this.state.programs];
        if (programs[0] == e.detail.value) {
            return;
        }
        programs[0] = e.detail.value;
        this.setState({programs: programs});
        this.props.dispatchUpdatePrograms(this.state.programs);
        this.addProgramToAll();
        this.publishCounter("bulkProgram1", "select")
    }

    handleChangeProgram2 = (e: KatDropdown.ChangeEvent) => {
        const programs = [...this.state.programs];
        if (programs[1] == e.detail.value) {
            return;
        }
        if (e.detail.value != SELECT_YOUR_PROGRAM) {
            programs[1] = e.detail.value;
        } else {
            programs.splice(1, 1);
        }
        this.setState({programs: programs});
        this.props.dispatchUpdatePrograms(this.state.programs);
        this.addProgramToAll();
        this.publishCounter("bulkProgram2", "select")
    }

    handleChangeSeason = (e: KatRadiobuttonGroup.ChangeEvent) => {
        this.setState({storageSeason: e.detail.value == "PEAK" ? StorageSeason.PEAK : StorageSeason.NON_PEAK});
        this.props.dispatchStorageSeason(this.state.storageSeason);

        // update all product's season
        const products: Array<ProductInfo> = [];
        this.props.bulkProductsRes.products.map((p) => {
            products.push({
                ...p,
                isNonPeakSelected: e.detail.value == "NON_PEAK",
            });
        })
        this.props.dispatchUpdateAllProducts(products);

        this.publishCounter("bulkStorageSeason", "click")
    }

    handlePageChange(event: KatPagination.PageChangedEvent) {
        const bulkProductSearchRes = this.props.bulkProductSearchRes.request;
        const request: BulkSearchProductRequest = {
            countryCode: bulkProductSearchRes?.countryCode ?? "US",
            keywords: bulkProductSearchRes?.keywords ?? "",
            searchType: bulkProductSearchRes?.searchType ?? SearchType.MERCHANT,
            filters: bulkProductSearchRes?.filter,
            sortOrder: bulkProductSearchRes?.sortOrder,
            sortedColumnId: bulkProductSearchRes?.sortedColumnId,
            pageOffset: event.detail.page
        }
        this.props.dispatchGetProduct(request);
        this.publishCounter("bulkProductSearchPageChange", "click")
    }

    publishCounter(method: string, counter: string) {
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter(method, counter);
        }
    }

    private getProgramDropdownOptions = (hasDefault: boolean): any[] => {
        const {t} = this.props;
        const options: any[] = [];
        if (hasDefault) {
            options.push({
                name: t("SC_FBA_Calc_dropdown_compare"),
                value: SELECT_YOUR_PROGRAM,
            })
        }
        if (this.props.currentCountryCode != undefined && this.props.currentCountryCode != "") {
            countryProgramList[this.props.currentCountryCode].map(program => {
                options.push({
                    name: t(bulkProgramName[program][0]),
                    value: program,
                })
            })
        }
        return options;
    }

    isProgramWithInventoryStoreParam(selectedProgram: string) {
        return programWithInventoryAndStoreParam.indexOf(selectedProgram) >= 0;
    }

    isProgramWithCeeParam(selectedProgram: string) {
        return programWithCeeParam.indexOf(selectedProgram) >= 0;
    }

    handleChangeCee(index: number, event: KatCheckbox.ChangeEvent) {
        let cee = this.state.cee;
        cee[index] = event.detail.checked;
        this.setState({cee: cee});
        // update defaultProgramInputMap for new adding products
        this.defaultProgramInputMap.set(this.state.programs[index],
            {
                ...this.defaultProgramInputMap.get(this.state.programs[index]),
                cee: event.detail.checked
            });
        // update all products
        const products: Array<ProductInfo> = [];
        this.props.bulkProductsRes.products.map((p) => {
            products.push({
                ...p,
                programInputMap: new Map(this.defaultProgramInputMap),
            });
        })
        this.props.dispatchUpdateAllProducts(products);
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("bulk_programParam_cee", "select");
        }

    }

    handleChangeInventory(index: number, event: KatDropdown.ChangeEvent) {
        let inventory = this.state.inventoryPlacedIn;
        inventory[index] = event.detail.value;
        this.setState({inventoryPlacedIn: inventory});
        // update defaultProgramInputMap for new adding products
        this.defaultProgramInputMap.set(this.state.programs[index],
            {
                ...this.defaultProgramInputMap.get(this.state.programs[index]),
                inventoryPlace: event.detail.value
            });
        // update all products
        const products: Array<ProductInfo> = [];
        this.props.bulkProductsRes.products.map((p) => {
            products.push({
                ...p,
                programInputMap: new Map(this.defaultProgramInputMap),
            });
        })
        this.props.dispatchUpdateAllProducts(products);
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("bulk_programParam_inventoryPlacedIn", "select");
        }
    }

    getInventoryStoreDropdown(selectedProgram: string, index: number) {
        const {t} = this.props;
        return (
            <>
                {this.isProgramWithInventoryStoreParam(selectedProgram) &&
                <>
                    <div className="bulk-search-inventory">
                        <KatDropdown rich-selection-label value={this.state.inventoryPlacedIn[index]}
                                     size="small" label={t("SC_FBA_Calc_label_inventoryPlacedIn")}
                            //@ts-ignore
                                     className="bulk-search-inventory-dropdown" onChange={e => this.handleChangeInventory(index, e)}>
                            {
                                getProgramInventoryCountryListConfig(selectedProgram).map((country, index) => (
                                    <KatOption value={country} key={index}>
                                        <div className="flexed">
                                            <KatIcon name={("flag-" + country.toLowerCase()) as KatIcon.Name} size="small"/>
                                            <span className="paddedMedium">{country}</span>
                                        </div>
                                    </KatOption>
                                ))
                            }
                        </KatDropdown>
                        {
                            // Now Core program is base on seller current status. So we only show it in other program.
                            // CEE means seller only have inventory in DE.
                            (this.state.inventoryPlacedIn[index] ?? "") == "DE" && this.isProgramWithCeeParam(selectedProgram) && <>
                                <KatCheckbox className="bulk-search-cee" label={t("SC_FBA_Calc_label_cee")} checked={this.state.cee[index]}
                                             onChange={e => this.handleChangeCee(index, e)}/>
                            </>
                        }
                    </div>
                </>
                }
            </>
        )
    }

    addProgramToAll = () => {
        // default programInputMap
        this.defaultProgramInputMap = new Map<string, ProgramInput>();
        this.state.programs.forEach((program, index) => {
                let programInputLet: ProgramInput = {};
                if (this.isProgramWithInventoryStoreParam(program)) {
                    programInputLet = {
                        ...programInputLet,
                        inventoryPlace: this.state.inventoryPlacedIn[index]
                    }
                }
                if (this.isProgramWithCeeParam(program)) {
                    programInputLet = {
                        ...programInputLet,
                        cee: this.state.cee[index]
                    }
                }
                const programInput = {...programInputLet};
                this.defaultProgramInputMap.set(program, programInput);
            }
        )

        const products: Array<ProductInfo> = [];
        this.props.bulkProductsRes.products.map((p) => {
            let programMap: Map<string, ProgramInput>;
            if (p.programInputMap == undefined || p.programInputMap == null) {
                programMap = new Map(this.defaultProgramInputMap);
            } else {
                programMap = new Map(p.programInputMap);
                // del unSelected
                programMap.forEach((v, k) => {
                    if (!this.defaultProgramInputMap.has(k)) {
                        programMap.delete(k);
                    }
                })
                // add new
                this.defaultProgramInputMap.forEach((v, k) => {
                    if (!programMap.has(k)) {
                        programMap.set(k, v);
                    }
                })
            }
            const product: ProductInfo = {
                ...p,
                isNonPeakSelected: this.state.storageSeason == StorageSeason.NON_PEAK,
                programInputMap: programMap
            }
            products.push(product);
        })
        this.props.dispatchUpdateAllProducts(products);
    }

    productInSelected = (product: ProductInfo) => {
        let res = false;
        this.props.bulkProductsRes.products.forEach(p => {
            if (p.merchantSku == product.merchantSku && p.asin == product.asin && p.fnsku == product.fnsku) {
                res = true;
            }
        });
        return res;
    }

    render() {
        const {t} = this.props;
        const productResults = this.getProducts();

        return (
            <>
                <KatTable key="product-table">
                    <KatTableHead>
                        <KatTableRow>
                            <KatTableCell>
                                <KatCheckbox onChange={e => this.handleSelectAll(e)}/>
                            </KatTableCell>
                            <KatTableCell>{t("SC_FBA_Calc_bulk_image")}</KatTableCell>
                            <KatTableCell>{t("SC_FBA_Calc_bulk_product_name")}
                                <p className="bulk-table-header-asin">{t("SC_FBA_Calc_productInfo_ASIN")}</p>
                            </KatTableCell>
                            <KatTableCell>{t("SC_FBA_Calc_bulk_product_dimensions")}</KatTableCell>
                            <KatTableCell>{t("SC_FBA_Calc_bulk_fee_category")}</KatTableCell>
                            <KatTableCell>{t("SC_FBA_Calc_bulk_fulfilled_by")}</KatTableCell>
                            <KatTableCell>{t("SC_FBA_Calc_bulk_price")}</KatTableCell>
                        </KatTableRow>
                    </KatTableHead>
                    <KatTableBody>
                        {
                            this.props.bulkProductsRes.products
                                .map((product, number) =>
                                    (this.productTableRow(product, product.asin + product.fnsku + product.merchantSku, number, false)))
                        }
                        {
                            productResults.products.filter(product => !this.productInSelected(product))
                                .map((product, number) =>
                                    (this.productTableRow(product, product.asin + product.fnsku + product.merchantSku, number + this.props.bulkProductsRes.products.length, this.props.bulkProductsRes.products.length >= BULK_MAX_SIZE)))
                        }
                    </KatTableBody>
                </KatTable>

                {this.props.bulkProductSearchRes.requestStatus == RequestStatus.SUCCESS &&
                <div id="product-list-pagination" className="pagination">
                    <KatPagination totalItems={this.props.bulkProductSearchRes.productData.data.totalProductCount}
                                   itemsPerPage={this.props.bulkProductSearchRes.request?.searchType == SearchType.MERCHANT ? MERCHANT_ITEM_PER_PAGE : GENERAL_ITEM_PER_PAGE}
                                   page={this.props.bulkProductSearchRes.productData.data.currentPage}
                                   onPageChanged={this.handlePageChange}/>
                </div>
                }

                <KatBox variant="ltgrey" className="bulk-search-product-table-program-box">
                    <KatDropdown rich-selection-label value={PROGRAM_NAME_OF_FBA} size="small" label={t("SC_FBA_Calc_bulk_program") + " 1"}
                                 className="bulk-search-program" options={this.getProgramDropdownOptions(false)}
                                 onChange={this.handleChangeProgram1}>
                    </KatDropdown>
                    {this.getInventoryStoreDropdown(this.state.programs[0], 0)}
                    <KatDropdown rich-selection-label value={SELECT_YOUR_PROGRAM} size="small" label={t("SC_FBA_Calc_bulk_program") + " 2"}
                                 className="bulk-search-program" options={this.getProgramDropdownOptions(true)}
                                 onChange={this.handleChangeProgram2}>
                    </KatDropdown>
                    {this.getInventoryStoreDropdown(this.state.programs[1], 1)}
                    <KatRadiobuttonGroup className="bulk-search-storage-season" name="storage season" value={this.state.storageSeason}
                                         label={t("SC_FBA_Calc_bulk_storage_season")} onChange={this.handleChangeSeason}
                                         options={[
                                             {label: t("SC_FBA_Calc_label_nonPeakPeriod"), value: StorageSeason.NON_PEAK},
                                             {label: t("SC_FBA_Calc_label_peakPeriod"), value: StorageSeason.PEAK}]}/>
                    <KatLabel className="bulk-search-product-table-product-num"
                              text={t("SC_FBA_Calc_info_productCount", {X: this.props.bulkProductsRes.products.length})}></KatLabel>
                    <KatButton label={t("SC_FBA_Calc_bulk_next")} variant="primary" className="bulk-search-step-next"
                               disabled={this.props.bulkProductsRes.products.length == 0 || this.props.bulkProductsRes.programs.length == 0}
                               onClick={() => {
                                   this.props.handleNext();
                               }}/>
                </KatBox>
            </>
        )
            ;
    }
}

export default withTranslation()(connector(BulkProductTableComponent));
